import { useEffect, useRef, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';

// Cage staff keep the dashboard open for a whole shift, so after a deploy
// they run on stale JS until they refresh (this caused a credit receipt to
// print in the old format from a stale tab). This banner polls the served
// index.html for a changed hashed bundle and prompts a refresh when the
// deployed build no longer matches the one this tab loaded.

const POLL_MS = 3 * 60 * 1000; // every 3 minutes
const BUNDLE_RE = /\/assets\/index-[A-Za-z0-9_-]+\.js/;

function extractBundle(text) {
  const m = text && text.match(BUNDLE_RE);
  return m ? m[0] : null;
}

export default function VersionBanner() {
  const [stale, setStale] = useState(false);
  const currentRef = useRef(null);

  useEffect(() => {
    // The bundle URL this tab was loaded with.
    const el = document.querySelector('script[src*="/assets/index-"]');
    currentRef.current = el ? extractBundle(el.getAttribute('src') || '') : null;
    // No hashed bundle (e.g. local dev server) → nothing to compare against.
    if (!currentRef.current) return undefined;

    let cancelled = false;
    const check = async () => {
      if (document.hidden || cancelled) return;
      try {
        const res = await fetch(`/index.html?ts=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const latest = extractBundle(await res.text());
        if (!cancelled && latest && latest !== currentRef.current) {
          setStale(true);
        }
      } catch {
        /* offline / transient — try again next tick */
      }
    };

    const id = setInterval(check, POLL_MS);
    const onVis = () => { if (!document.hidden) check(); };
    document.addEventListener('visibilitychange', onVis);
    const t = setTimeout(check, 15000); // first check shortly after load

    return () => {
      cancelled = true;
      clearInterval(id);
      clearTimeout(t);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  if (!stale) return null;

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] flex items-center gap-3 bg-amber-500 text-black px-4 py-3 rounded-lg shadow-2xl border border-amber-600 max-w-[92vw]">
      <RefreshCw size={18} className="flex-shrink-0" />
      <span className="text-sm font-medium">
        A new version of the dashboard is available.
      </span>
      <button
        onClick={() => window.location.reload()}
        className="px-3 py-1 bg-black text-amber-400 rounded font-semibold text-sm hover:bg-gray-900 whitespace-nowrap"
      >
        Refresh now
      </button>
      <button
        onClick={() => setStale(false)}
        className="text-black/70 hover:text-black"
        aria-label="Dismiss"
      >
        <X size={18} />
      </button>
    </div>
  );
}
